import { DateTime } from 'luxon';

// Helpers de fecha.
//
// Los parámetros de fecha que salen hacia la API y hacia la URL se envían
// como "YYYY-MM-DD". El backend interpreta ese string con su propia lógica
// de límites de día. filterSince/filterUntil generan ISO con offset para
// contextos que sí necesitan precisión de zona horaria.
//
// Nota sobre Polaris DatePicker / Date objects: cuando un `Date` llega desde
// un calendar picker del navegador, sus componentes locales (getFullYear /
// getMonth / getDate) son lo que el usuario vio en pantalla. El instante UTC
// del Date no es fiable como "el 10 de abril en la zona del usuario", porque
// depende de la zona del OS del navegador. Por eso `toLuxonCivilInZone`
// trata los Date como **componentes civiles** y los reinterpreta en `tz`.

const HAS_OFFSET_RE = /([zZ]|[+-]\d{2}:?\d{2})$/;

const toLuxonCivilInZone = (input, zone) => {
  if (input == null || input === '') return null;

  if (input instanceof DateTime) {
    if (!input.isValid) return null;
    return DateTime.fromObject({ year: input.year, month: input.month, day: input.day }, { zone });
  }

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null;
    return DateTime.fromObject(
      { year: input.getFullYear(), month: input.getMonth() + 1, day: input.getDate() },
      { zone },
    );
  }

  if (typeof input === 'string') {
    if (HAS_OFFSET_RE.test(input)) {
      // ISO con offset: tomamos sus componentes civiles ya en `zone` y los
      // usamos como día civil. Nunca debería entrar por aquí en uso normal —
      // los filtros se construyen a partir de Date objects del picker — pero
      // lo soportamos por robustez ante URLs legadas.
      const parsed = DateTime.fromISO(input, { setZone: true });
      if (!parsed.isValid) return null;
      const inZone = parsed.setZone(zone);
      return DateTime.fromObject({ year: inZone.year, month: inZone.month, day: inZone.day }, { zone });
    }
    // "YYYY-MM-DD" (o ISO civil sin zona): civil directo en `zone`.
    const parsed = DateTime.fromISO(input, { zone });
    return parsed.isValid ? parsed : null;
  }

  return null;
};

// Inicio de día civil en `tz` → ISO con offset. Para filtros `since`.
export const filterSince = (dateInput, tz) => {
  const dt = toLuxonCivilInZone(dateInput, tz);
  return dt ? dt.startOf('day').toISO() : null;
};

// Fin de día civil en `tz` (23:59:59.999) → ISO con offset. Para filtros `until`.
export const filterUntil = (dateInput, tz) => {
  const dt = toLuxonCivilInZone(dateInput, tz);
  return dt ? dt.endOf('day').toISO() : null;
};

// Fecha única (single-date pickers). Alias semántico de filterSince.
export const filterDate = (dateInput, tz) => filterSince(dateInput, tz);

// Lee un parámetro crudo (URL, legado, form) y devuelve un Date con
// componentes locales equivalentes al día civil en `tz`, apto para alimentar
// los calendar pickers del navegador.
//
// Acepta:
//   - ISO con offset / Z     → se traduce al día civil en `tz` y se emite
//                              como Date(año, mes, día) local.
//   - "YYYY-MM-DD"            → Date(año, mes, día) local.
//   - Date / DateTime         → pasan a través (se normalizan igual).
//   - null / undefined / ""   → null.
export const parseFilterParam = (raw, tz) => {
  const dt = toLuxonCivilInZone(raw, tz);
  if (!dt) return null;
  return new Date(dt.year, dt.month - 1, dt.day);
};

// Formatea un Date (o Luxon DateTime) como "YYYY-MM-DD" usando sus componentes civiles.
// Uso principal: escribir parámetros de fecha en URL y en llamadas a la API.
export const toYMD = (date) => {
  if (!date) return null;
  if (date instanceof DateTime) {
    if (!date.isValid) return null;
    return date.toFormat('yyyy-MM-dd');
  }
  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof date === 'string') return date.slice(0, 10) || null;
  return null;
};

// "Hoy" como Date con componentes locales = día civil actual en `tz`.
export const todayInTz = (tz) => {
  const now = DateTime.now().setZone(tz);
  return new Date(now.year, now.month - 1, now.day);
};

// Display: ISO UTC del backend → string formateado en `tz`.
//
// Casos de entrada:
//   - "YYYY-MM-DD" (10 chars): día civil ya en zona del negocio. NO se le
//     aplica conversión de zona, porque eso restaría 6h en MX y devolvería el
//     día anterior. Se formatea con sus componentes Y/M/D directos.
//   - ISO con offset / Z: se trata como instante UTC y se re-zonifica a `tz`.
//   - null/undefined/"": cadena vacía.
const ONLY_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const displayInTz = (isoUtc, tz, format = 'dd/MM/yyyy') => {
  if (isoUtc == null || isoUtc === '') return '';

  if (typeof isoUtc === 'string' && ONLY_DAY_RE.test(isoUtc)) {
    const dt = DateTime.fromISO(isoUtc); // sin setZone → solo civil
    return dt.isValid ? dt.toFormat(format) : '';
  }

  const dt = DateTime.fromISO(isoUtc, { setZone: true });
  if (!dt.isValid) return '';
  return dt.setZone(tz).toFormat(format);
};
